/**
 * 将店铺「本地」尺码库（通常由官方库复制而来）提交到 TikTok 可复用尺码表，
 * 成功后由调用方删除本地行，避免与官方库 / 平台库重复。
 */
import { fixedUi } from '@/i18n/fixedUi';
import {
  isOfficialSizeChartTemplatePayload,
  recordsFromTiktokSizeChartData,
  tiktokSizeChartId,
} from '@/tiktok-platform/utils/tiktokOfficialSizeChart';

function pickSizeChartData(payload = {}, item = {}) {
  return payload.sizeChartData
    || payload.size_chart_data
    || payload.officialSizeChart?.sizeChartData
    || (Array.isArray(payload.sizeChartDatas)
      ? (payload.sizeChartDatas[0]?.size_chart_data || payload.sizeChartDatas[0]?.sizeChartData)
      : null)
    || item.sizeChartData
    || null;
}

/**
 * @param {{ shopId: string|number, item: object, api: object }} args
 * @returns {Promise<{ sizeChartId: string, alreadySynced: boolean }>}
 */
export async function submitLocalTiktokSizeChartToPlatform({
  shopId,
  item = {},
  api = null,
} = {}) {
  if (!shopId || !api) throw new Error(fixedUi('缺少店铺或尺码表接口'));
  const payload = item?.payload && typeof item.payload === 'object' ? item.payload : {};
  if (!isOfficialSizeChartTemplatePayload(payload) && !pickSizeChartData(payload, item)) {
    throw new Error(fixedUi('该模板没有可提交的尺码表内容'));
  }

  const existingId = tiktokSizeChartId(
    item.platformBusinessId
    || payload.platformBusinessIds?.[0]
    || payload.officialSizeChart?.selectedBusinessIds?.[0],
  );
  if (existingId) {
    return { sizeChartId: existingId, alreadySynced: true };
  }

  const sizeChartData = pickSizeChartData(payload, item);
  if (!sizeChartData || typeof sizeChartData !== 'object') {
    // 仅有 records 时尽量转回 table（发品草稿形态）
    const records = Array.isArray(payload.officialSizeChart?.records)
      ? payload.officialSizeChart.records
      : [];
    if (!records.length) {
      throw new Error(fixedUi('本地尺码表缺少 sizeChartData，请先编辑完善后再提交'));
    }
    throw new Error(fixedUi('本地尺码表缺少平台结构化内容，请打开编辑后重新保存再提交'));
  }

  // 校验 table 至少有一行
  const converted = recordsFromTiktokSizeChartData(sizeChartData, {
    groupList: payload.officialSizeChart?.groupList || [{ id: 1 }],
    elementList: payload.officialSizeChart?.elementList || [],
  });
  if (!converted?.records?.length && !sizeChartData?.table?.rows?.length) {
    throw new Error(fixedUi('本地尺码表没有尺寸行，无法提交平台'));
  }

  const categoryId = String(
    payload.categoryId || item.categoryId || payload.productTypeId || item.productTypeId || '',
  ).trim();
  if (!categoryId) throw new Error(fixedUi('缺少类目，无法提交平台尺码表'));

  const created = await api.createPublishSizeChart(shopId, {
    name: String(item.name || payload.name || '尺码表').trim() || '尺码表',
    styleNo: String(item.styleNo || 'SIZE').trim() || 'SIZE',
    categoryId,
    publishToPlatform: true,
    sizeChartMode: 'REUSABLE_TEMPLATE',
    sizeChartData,
  });
  if (created?.platformWriteSkipped) {
    const detail = String(created.platformError || created.message || '').trim();
    throw new Error(detail || fixedUi('平台尺码表写入不可用'));
  }
  const sizeChartId = tiktokSizeChartId(
    created?.platformBusinessId || created?.sizeChartId || created?.item?.platformBusinessId,
  );
  if (!sizeChartId) throw new Error(fixedUi('平台未返回尺码表 ID'));
  return { sizeChartId, alreadySynced: false };
}
